"use client";

import Image from "next/image";
import { useState } from "react";
import { Plus, RotateCcw } from "lucide-react";
import { DeviceStage, IphoneFrame, IpadFrame } from "./device-frames";
import { useSelfDemo } from "./use-self-demo";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Card 3's screen: the self-order phone and the POS tablet, side by side and
 * wired to each other.
 *
 * Tapping + on the phone's menu pushes the item straight onto the POS ticket,
 * which flashes and re-totals. That hand-off is the whole claim of the card —
 * the order reaches the kitchen without anyone writing it down — so it's the
 * one thing the mock-up lets you actually do.
 *
 * Palette: brand blue #0060A8 owns the chrome (POS header, cart bar, add
 * buttons); yellow #FFCC33 marks what just happened — the qty badge on each
 * ticket line and the count in the cart. Yellow only sits under deep-blue ink.
 */

type MenuItem = {
  id: string;
  /** Name lives in `home.mocks.pos.menu.<id>`. */
  price: number;
  /** 240px square thumb, pre-cropped by scripts — see public/v2/menu-*. */
  img: string;
};

const MENU: MenuItem[] = [
  { id: "kaprao", price: 60, img: "/v2/menu-kaprao.webp" },
  { id: "chicken", price: 55, img: "/v2/menu-chicken.webp" },
  { id: "tea", price: 35, img: "/v2/menu-chayen.webp" },
];

/** The ticket opens with two lines so the POS never reads as broken/empty. */
const OPENING_TICKET = [
  { id: "kaprao", qty: 2 },
  { id: "chicken", qty: 1 },
];

/**
 * The self-demo taps these two in order: a drink the ticket has never seen
 * (a new line lands) and then a dish already on it (the count ticks up), so
 * both halves of "the order just arrives" are shown.
 */
const TOUR = ["tea", "kaprao"];

export default function PosMock({ active }: { active?: boolean }) {
  const { t } = useTranslation();
  /** Menu names live in the bundle; the id is what the mock keys off. */
  const itemName = (id: string) => t(`home.mocks.pos.menu.${id}`);
  const [lines, setLines] = useState<{ id: string; qty: number }[]>(
    () => OPENING_TICKET,
  );
  /** Which line just changed — drives the one-shot highlight on the ticket. */
  const [flash, setFlash] = useState<string | null>(null);

  // Loops, so a reader arriving after the first pass still sees an order come
  // in rather than a ticket that was already full when they got here. The
  // adds alone can't loop — quantities would climb forever — so the run ends
  // by closing the bill, which is also the beat that gives the finished
  // ticket its own moment on screen before the next order starts.
  const { ref: screenRef, cancel } = useSelfDemo<HTMLDivElement>(
    [
      ...TOUR.map((id) => () => {
        const item = MENU.find((m) => m.id === id);
        if (item) add(item);
      }),
      () => {
        setLines(OPENING_TICKET);
        setFlash(null);
      },
    ],
    { active, loop: true },
  );

  function add(item: MenuItem) {
    setLines((current) => {
      const at = current.findIndex((l) => l.id === item.id);
      if (at === -1) return [...current, { id: item.id, qty: 1 }];
      return current.map((l, i) => (i === at ? { ...l, qty: l.qty + 1 } : l));
    });
    setFlash(item.id);
  }

  /** Reader-driven add — takes the demo's remaining taps off the clock. */
  function tap(item: MenuItem) {
    cancel();
    add(item);
  }

  const total = lines.reduce((sum, l) => {
    const item = MENU.find((m) => m.id === l.id);
    return sum + (item ? item.price * l.qty : 0);
  }, 0);
  const count = lines.reduce((n, l) => n + l.qty, 0);
  /** The dish behind the current highlight, for the floating order alert. */
  const flashed = MENU.find((m) => m.id === flash);

  return (
    <DeviceStage>
      {/* The tablet is the stage; the phone laps over its right edge like
          someone holding it up to the counter. The overlap is kept to roughly
          the tablet's own bezel width — enough to read as layered depth,
          little enough that the phone never covers a ticket price, which is
          the number this card is selling. */}
      <IpadFrame className="w-[74%]">
        <div ref={screenRef} className="flex h-full flex-col bg-[#F4F8FC]">
          {/* POS chrome in the brand blue. */}
          <div className="flex items-center justify-between bg-[linear-gradient(115deg,#00518D_0%,#0060A8_100%)] px-[0.6em] py-[0.4em]">
            <div>
              <p className="text-[0.72em] font-semibold text-white">
                {t("home.mocks.pos.newOrder")}
              </p>
              <p className="text-[0.52em] text-white/70">{t("home.mocks.pos.table")}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                cancel();
                setLines(OPENING_TICKET);
                setFlash(null);
              }}
              aria-label={t("home.mocks.pos.clearAria")}
              className="flex items-center gap-[0.25em] rounded-full bg-white/12 px-[0.55em] py-[0.25em] text-[0.52em] font-medium text-white transition-colors hover:bg-white/25"
            >
              <RotateCcw aria-hidden className="size-[0.9em]" />
              {t("home.mocks.pos.clear")}
            </button>
          </div>

          <ul className="flex-1 space-y-[0.3em] overflow-hidden p-[0.55em]">
            {lines.map((line) => {
              const item = MENU.find((m) => m.id === line.id);
              if (!item) return null;
              return (
                <li
                  // Re-keying on qty replays the flash when an existing line
                  // is bumped, not just when a new one lands.
                  key={`${line.id}-${line.qty}`}
                  className={`flex items-center gap-[0.4em] rounded-[0.4em] px-[0.4em] py-[0.3em] text-[0.62em] shadow-[0_0.1em_0.3em_-0.1em_rgba(0,96,168,0.25)] ${
                    flash === line.id ? "ps-mock-land bg-[#FFF6D9]" : "bg-white"
                  }`}
                >
                  <Image
                    src={item.img}
                    alt=""
                    width={48}
                    height={48}
                    className="size-[1.7em] shrink-0 rounded-[0.3em] object-cover"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-800">
                      {itemName(item.id)}
                    </span>
                    <span className="block text-[0.8em] text-gray-400">
                      ฿{item.price} × {line.qty}
                    </span>
                  </span>
                  <span className="grid size-[1.4em] shrink-0 place-items-center rounded-[0.3em] bg-[#FFCC33] text-[0.75em] font-bold text-[#00518D]">
                    {line.qty}
                  </span>
                  <span className="w-[2.6em] text-right font-semibold text-[#00518D]">
                    ฿{item.price * line.qty}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between border-t border-[#0060A8]/10 bg-white px-[0.6em] py-[0.4em]">
            <span className="flex items-center gap-[0.3em] text-[0.55em] font-medium text-gray-500">
              <span className="jd-live-blink size-[0.55em] rounded-full bg-[#FFCC33]" />
              {t("home.mocks.pos.toKitchen")}
            </span>
            <span className="text-[0.82em] font-bold text-[#00518D]">
              ฿{total}
            </span>
          </div>
        </div>
      </IpadFrame>

      <IphoneFrame className="absolute -bottom-[6%] right-0 w-[29%]">
        <div className="flex h-full flex-col bg-[#FBFDFF]">
          <div className="px-[0.5em] pb-[0.35em] pt-[1.5em]">
            <p className="text-[0.6em] font-semibold text-[#00518D]">
              {t("home.mocks.pos.order")}
            </p>
            <p className="text-[0.48em] text-gray-400">{t("home.mocks.pos.tableShort")}</p>
          </div>

          <ul className="flex-1 space-y-[0.3em] px-[0.4em]">
            {MENU.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-[0.35em] rounded-[0.4em] bg-white p-[0.28em] shadow-[0_0.1em_0.3em_-0.1em_rgba(0,96,168,0.3)]"
              >
                <Image
                  src={item.img}
                  alt=""
                  width={48}
                  height={48}
                  className="size-[1.6em] shrink-0 rounded-[0.3em] object-cover"
                />
                <span className="min-w-0 flex-1">
                  {/* Two lines rather than an ellipsis: the phone column is
                      narrow enough that truncating cuts most Thai dish names
                      mid-word. */}
                  <span className="block line-clamp-2 text-[0.5em]/[1.3] font-medium text-gray-800">
                    {itemName(item.id)}
                  </span>
                  <span className="block text-[0.5em] font-semibold text-[#00518D]">
                    ฿{item.price}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => tap(item)}
                  aria-label={t("home.mocks.pos.addAria", { name: itemName(item.id) })}
                  className="grid size-[1.6em] shrink-0 place-items-center rounded-full bg-[#0060A8] text-white transition-transform hover:scale-110 active:scale-95"
                >
                  <Plus aria-hidden className="size-[0.9em]" strokeWidth={3} />
                </button>
              </li>
            ))}
          </ul>

          <div className="m-[0.4em] flex items-center justify-between rounded-full bg-[linear-gradient(115deg,#00518D_0%,#0060A8_100%)] px-[0.5em] py-[0.28em] text-white">
            <span className="text-[0.5em] font-medium">{t("home.mocks.pos.cart")}</span>
            <span
              key={count}
              className="ps-mock-pop rounded-full bg-[#FFCC33] px-[0.5em] py-[0.1em] text-[0.5em] font-bold text-[#00518D]"
            >
              {t("home.mocks.pos.items", { count })}
            </span>
          </div>
        </div>
      </IphoneFrame>

      {/* The kitchen's side of the tap, floated over the tablet. It appears
          the moment an order lands rather than on a delay — unlike card 2's
          notification, this one IS the event, not a consequence of it — and
          re-keys on the running item count so every tap replays it. */}
      {flashed && (
        <div
          key={`${flash}-${count}`}
          // Same single size knob as card 2's notification (crm-mock):
          // everything inside is em-based, so this scales the card without
          // touching the devices it floats over.
          className="ps-mock-notify absolute -bottom-[9%] -left-[5%] flex items-center gap-[0.55em] text-[1.3em] rounded-[0.9em] border border-black/5 bg-white py-[0.4em] pl-[0.4em] pr-[0.9em] shadow-[0_0.9em_2em_-0.5em_rgba(15,23,42,0.45)]"
        >
          <Image
            src={flashed.img}
            alt=""
            width={48}
            height={48}
            className="size-[2.8em] shrink-0 rounded-[0.6em] object-cover"
          />
          <span>
            <span className="flex items-center gap-[0.35em] text-[0.68em] font-semibold text-gray-900">
              {t("home.mocks.pos.orderArrived")}
              <span className="rounded-full bg-[#FFCC33] px-[0.5em] py-[0.05em] text-[0.75em] font-bold text-[#00518D]">
                {t("home.mocks.pos.tableShort")}
              </span>
            </span>
            <span className="block text-[0.58em] text-gray-500">
              {itemName(flashed.id)} · {t("home.mocks.pos.sentToKitchen")}
            </span>
          </span>
        </div>
      )}
    </DeviceStage>
  );
}
